import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import PerfectScrollbar from 'react-perfect-scrollbar';

import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import bytesToSize from 'utils/bytesToSize';

import Button from './Button';

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024;

const TYPE_LABELS = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/msword': 'DOC',
  'application/pdf': 'PDF',
  'application/csv': 'CSV',
  'application/x-csv': 'CSV',
  'text/csv': 'CSV',
  'text/x-csv': 'CSV',
  'text/comma-separated-values': 'CSV',
  'text/x-comma-separated-values': 'CSV',
  'text/html': 'HTML',
  'image/jpg': 'JPG',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/webp': 'WEBP',
  'image/*': 'imagens',
};

const i18n = {
  'pt-br': {
    instructions: 'Solte os arquivos aqui ou clique para escolher',
    full: 'Limite de arquivos atingido',
    invalidType: 'formato não suportado',
    tooLarge: 'arquivo muito grande',
    invalidFile: 'não foi possível adicionar este arquivo',
    removeAll: 'Remover todos',
    remove: 'Remover',
    uploadFiles: 'Enviar',
    next: 'Próximo',
    file: 'arquivo',
    files: 'arquivos',
  },
};

const getFileId = file => `${file.name}-${file.lastModified}-${file.size}`;

const getTypeLabels = acceptTypes => {
  if (!acceptTypes) return [];

  const labels = acceptTypes
    .split(',')
    .map(type => type.trim())
    .filter(Boolean)
    .map(type => {
      if (TYPE_LABELS[type]) return TYPE_LABELS[type];
      if (type.startsWith('.')) return type.slice(1).toUpperCase();

      return type.split('/').pop().split('+')[0].split('.').pop().toUpperCase();
    });

  return [...new Set(labels)];
};

const groupRejections = (rejections, messages, sizeLimit) => {
  const reasons = {
    'file-too-large': `${messages.tooLarge} (máximo de ${bytesToSize(sizeLimit)})`,
    'file-invalid-type': messages.invalidType,
  };
  const byReason = new Map();

  rejections.forEach(({ file, errors }) => {
    const reason = reasons[errors[0]?.code] || messages.invalidFile;
    byReason.set(reason, [...(byReason.get(reason) || []), file.name]);
  });

  return [...byReason].map(([reason, names]) =>
    names.length === 1
      ? `${names[0]}: ${reason}.`
      : `${names.length} arquivos ignorados: ${reason}.`,
  );
};

const describeConstraints = (acceptTypes, sizeLimit, maxFiles, messages) => {
  const typeLabels = getTypeLabels(acceptTypes);

  return [
    typeLabels.length && typeLabels.join(', '),
    `até ${bytesToSize(sizeLimit)} cada`,
    maxFiles && `máximo de ${maxFiles} ${messages.files}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

const describeSelection = (files, maxFiles, messages) => {
  const unit = files.length === 1 ? messages.file : messages.files;
  const count = maxFiles ? `${files.length} de ${maxFiles}` : `${files.length}`;
  const totalSize = files.reduce((total, file) => total + file.size, 0);

  return `${count} ${unit} · ${bytesToSize(totalSize)}`;
};

const describeDuplicates = names => {
  if (!names.length) return '';
  if (names.length === 1) return `${names[0]} já estava na lista.`;

  return `${names.length} arquivos já estavam na lista.`;
};

const describeOverflow = (discarded, maxFiles) => {
  if (discarded < 1) return '';

  const subject = discarded === 1 ? 'arquivo ficou' : 'arquivos ficaram';

  return `${discarded} ${subject} de fora: o limite é de ${maxFiles}.`;
};

const splitDuplicates = (picked, incoming) => {
  const seen = new Set(picked.map(getFileId));
  const duplicates = [];
  const fresh = incoming.filter(file => {
    const id = getFileId(file);

    if (seen.has(id)) {
      duplicates.push(file.name);

      return false;
    }
    seen.add(id);

    return true;
  });

  return { fresh, duplicates };
};

const useStyles = makeStyles(theme => ({
  root: {},
  dropZone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gridGap: theme.spacing(0.5),
    transition: theme.transitions.create(['background-color', 'border-color']),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
    },
  },
  dragActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  disabled: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  list: {
    maxHeight: 280,
  },
  item: {
    paddingLeft: 0,
    paddingRight: theme.spacing(6),
  },
  preview: {
    width: 40,
    height: 40,
    flexShrink: 0,
    marginRight: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  thumb: {
    objectFit: 'cover',
    display: 'block',
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
  actions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    '& > * + *': {
      marginLeft: theme.spacing(2),
    },
  },
}));

function FilePreview({ file }) {
  const classes = useStyles();
  const [src, setSrc] = useState('');

  useEffect(() => {
    if (!file.type?.startsWith('image/')) return undefined;

    const url = URL.createObjectURL(file);
    setSrc(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!src) {
    return (
      <div className={clsx(classes.preview, classes.placeholder)}>
        <Icon>description</Icon>
      </div>
    );
  }

  return <img className={clsx(classes.preview, classes.thumb)} src={src} alt={file.name} />;
}

FilePreview.propTypes = {
  file: PropTypes.object.isRequired,
};

function FileList({ files, itemActions, removeLabel, onRemove }) {
  const classes = useStyles();

  return (
    <PerfectScrollbar options={{ suppressScrollX: true }}>
      <List className={classes.list}>
        {files.map((file, index) => (
          <ListItem
            className={classes.item}
            divider={index < files.length - 1}
            key={getFileId(file)}>
            <FilePreview file={file} />
            <ListItemText
              primary={file.name}
              primaryTypographyProps={{ variant: 'body1', noWrap: true }}
              secondary={bytesToSize(file.size)}
            />
            <ListItemSecondaryAction>
              {itemActions.map(action => (
                <Tooltip key={action.label} title={action.label}>
                  <IconButton size="small" onClick={() => action.onClick(file, index)}>
                    <Icon fontSize="small">{action.icon}</Icon>
                  </IconButton>
                </Tooltip>
              ))}
              <Tooltip title={removeLabel}>
                <IconButton edge="end" size="small" onClick={() => onRemove(index)}>
                  <Icon fontSize="small">delete_outline</Icon>
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </PerfectScrollbar>
  );
}

FileList.propTypes = {
  files: PropTypes.array.isRequired,
  itemActions: PropTypes.array.isRequired,
  removeLabel: PropTypes.string,
  onRemove: PropTypes.func.isRequired,
};

function FilesDropzone(props) {
  const {
    className,
    localization,
    loading,
    acceptTypes,
    maxFiles,
    maxSize,
    nextStep,
    skipSendStep,
    onSubmit,
    onChange,
    value,
    itemActions,
    ...restProps
  } = props;

  // Hooks
  const classes = useStyles();

  // States
  const [internalFiles, setInternalFiles] = useState([]);
  const [notice, setNotice] = useState('');

  // Constants
  const messages = i18n[localization] || i18n['pt-br'];
  const isControlled = value !== undefined;
  const files = isControlled ? value : internalFiles;
  const sizeLimit = maxSize || DEFAULT_MAX_SIZE;
  const remaining = maxFiles ? Math.max(0, maxFiles - files.length) : Infinity;
  const isFull = remaining === 0;
  const canRemoveAll = !skipSendStep || !!onChange;
  const constraints = describeConstraints(acceptTypes, sizeLimit, maxFiles, messages);
  const summary = describeSelection(files, maxFiles, messages);

  // Actions
  const commit = nextFiles => {
    if (!isControlled) setInternalFiles(nextFiles);
    onChange?.(nextFiles);
  };

  const handleDrop = (acceptedFiles, rejections) => {
    const { fresh, duplicates } = splitDuplicates(files, acceptedFiles);
    const accepted = fresh.slice(0, remaining);
    const discarded = fresh.length - accepted.length;

    setNotice(
      [
        ...groupRejections(rejections, messages, sizeLimit),
        describeDuplicates(duplicates),
        describeOverflow(discarded, maxFiles),
      ]
        .filter(Boolean)
        .join(' '),
    );

    if (!accepted.length) return;
    if (skipSendStep) onSubmit?.(accepted, { onFinish: () => commit([]) });

    commit(files.concat(accepted));
  };

  const handleRemove = index => {
    setNotice('');
    commit(files.filter((file, current) => current !== index));
  };

  const handleRemoveAll = () => {
    setNotice('');
    commit([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: acceptTypes,
    maxSize: sizeLimit,
    disabled: isFull,
  });

  return (
    <div className={clsx(classes.root, className)} {...restProps}>
      <div
        className={clsx(classes.dropZone, {
          [classes.dragActive]: isDragActive,
          [classes.disabled]: isFull,
        })}
        {...getRootProps()}>
        <input {...getInputProps()} />
        <Icon color="action" fontSize="large">
          cloud_upload
        </Icon>
        <Typography color="textPrimary" variant="body1">
          {isFull ? messages.full : messages.instructions}
        </Typography>
        {constraints && (
          <Typography color="textSecondary" variant="caption">
            {constraints}
          </Typography>
        )}
      </div>
      {!!notice && (
        <Box mt={1}>
          <Typography color="error" variant="caption">
            {notice}
          </Typography>
        </Box>
      )}
      {!!files.length && (
        <>
          <Box mt={1}>
            <Typography color="textSecondary" variant="caption">
              {summary}
            </Typography>
          </Box>
          <FileList
            files={files}
            itemActions={itemActions}
            removeLabel={messages.remove}
            onRemove={handleRemove}
          />
          {canRemoveAll && (
            <div className={classes.actions}>
              <Button onClick={handleRemoveAll} variant="outlined" size="small">
                {messages.removeAll}
              </Button>
              {!skipSendStep && (
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  loading={loading}
                  onClick={() => onSubmit?.(files, { onFinish: () => commit([]) })}>
                  {nextStep ? messages.next : messages.uploadFiles}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

FilesDropzone.propTypes = {
  onSubmit: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.array,
  itemActions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  loading: PropTypes.bool,
  acceptTypes: PropTypes.string,
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number,
  nextStep: PropTypes.bool,
  skipSendStep: PropTypes.bool,
  className: PropTypes.string,
  localization: PropTypes.string,
};

FilesDropzone.defaultProps = {
  localization: 'pt-br',
  itemActions: [],
};

export default FilesDropzone;
